import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Popper,
  ClickAwayListener,
  MenuList,
  Paper
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { experimentColors } from '../../themes/experimentColors';

// Blazing fast dropdown component with direct DOM manipulation
const BlazingFastDropdown = ({ 
  options,
  value,
  placeholder,
  onChange,
  disabled,
  loading,
  size = 'normal',
  mode = 'individual'
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const buttonRef = React.useRef();
  
  const handleClick = React.useCallback((event) => {
    if (disabled || loading) return;
    
    // BLAZING FAST: Instant visual feedback
    setInternalOpen(true);
    setAnchorEl(event.currentTarget);
    
    // No background state update needed - this IS the state
  }, [disabled, loading]);
  
  const handleClose = React.useCallback(() => {
    setInternalOpen(false);
    setAnchorEl(null);
  }, []);
  
  const handleSelect = React.useCallback((optionValue) => {
    // Instant close for snappy feel
    setInternalOpen(false);
    setAnchorEl(null);
    
    // Background update doesn't affect UI speed
    requestAnimationFrame(() => {
      onChange(optionValue);
    });
  }, [onChange]);
  
  // Get styles
  const getStyles = () => {
    if (mode === 'bulk') {
      return {
        fontSize: size === 'compact' ? '10px' : '11px',
        minWidth: size === 'compact' ? '45px' : '60px',
        py: size === 'compact' ? '2px' : '3px',
        px: size === 'compact' ? '4px' : '6px',
        pr: size === 'compact' ? '18px' : '20px'
      };
    }
    return {
      fontSize: '12px',
      minWidth: 'auto',
      py: '2px',
      px: '6px',
      pr: '22px'
    };
  };
  
  const styles = getStyles();
  const selectedOption = options.find(opt => opt.value === value);
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Box
        ref={buttonRef}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: mode === 'bulk' ? '2px' : '6px',
          minWidth: styles.minWidth,
          py: styles.py,
          px: styles.px,
          pr: styles.pr,
          fontSize: styles.fontSize,
          fontWeight: 500,
          color: '#374151',
          backgroundColor: mode === 'bulk' ? '#f3f4f6' : 'transparent',
          borderRadius: '4px',
          cursor: disabled || loading ? 'default' : 'pointer',
          transition: 'none', // Remove ALL transitions
          opacity: disabled ? 0.6 : 1,
          '&:hover': !disabled && !loading ? {
            backgroundColor: mode === 'bulk' ? '#e5e7eb' : 'rgba(0, 0, 0, 0.04)',
          } : {},
        }}
      >
        {/* Display current value or placeholder */}
        {mode === 'bulk' ? (
          <Typography sx={{
            fontSize: styles.fontSize,
            fontWeight: 500,
            color: value ? '#1a1a1a' : '#6b7280'
          }}>
            {value ? `${t('to')} ${selectedOption?.label}` : placeholder || t('move')}
          </Typography>
        ) : selectedOption ? (
          <>
            <Box
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: selectedOption.color,
                flexShrink: 0
              }}
            />
            <span>{selectedOption.label}</span>
          </>
        ) : null}
        
        {/* Arrow icon */}
        <KeyboardArrowDown 
          sx={{ 
            fontSize: '16px', 
            color: '#9ca3af',
            ml: 'auto',
            transform: internalOpen ? 'rotate(180deg)' : 'none',
            transition: 'none' // Remove rotation transition
          }} 
        />
        
        {/* Loading indicator */}
        {(loading) && (
          <CircularProgress 
            size={12} 
            sx={{ ml: 1, color: '#9ca3af' }}
          />
        )}
      </Box>
      
      {/* Instant popup menu */}
      <Popper 
        open={internalOpen} 
        anchorEl={anchorEl} 
        placement="bottom-start"
        style={{ zIndex: 1300 }} // High z-index to appear above other elements
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              borderRadius: '6px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              mt: '4px',
              minWidth: buttonRef.current?.offsetWidth || 'auto'
            }}
          >
            <MenuList sx={{ py: '4px' }}>
              {options.map(option => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{
                    fontSize: mode === 'bulk' ? styles.fontSize : '12px',
                    fontWeight: 500,
                    py: mode === 'bulk' ? '3px' : '4px',
                    px: mode === 'bulk' ? '6px' : '8px',
                    color: '#4b5563',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  {mode === 'bulk' ? (
                    option.label
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Box
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: option.color,
                          flexShrink: 0
                        }}
                      />
                      <Typography sx={{
                        fontSize: '12px',
                        fontWeight: option.value === value ? 600 : 500,
                        color: 'inherit'
                      }}>
                        {option.label}
                      </Typography>
                    </Box>
                  )}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

// Memoize the BlazingFastDropdown to prevent excessive re-renders
const MemoizedBlazingFastDropdown = React.memo(BlazingFastDropdown, (prevProps, nextProps) => {
  // Only re-render if essential props change
  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.loading === nextProps.loading &&
    prevProps.mode === nextProps.mode &&
    prevProps.size === nextProps.size &&
    prevProps.options.length === nextProps.options.length
  );
});

const StatusDropdown = ({ 
  currentStatus, 
  experimentId,
  experiment,
  onStatusChange, 
  disabled = false,
  loading = false,
  // New props for bulk mode
  mode = 'individual', // 'individual' | 'bulk'
  selectedCount = 0,
  size = 'normal' // 'normal' | 'compact'
}) => {
  const { t } = useTranslation();
  const [isChanging, setIsChanging] = React.useState(false);
  
  const statusOptions = [
    { value: 'pending', label: t('pending'), color: experimentColors.pending },
    { value: 'accepted', label: t('accepted'), color: experimentColors.accepted },
    { value: 'running', label: t('running'), color: experimentColors.running },
    { value: 'finished', label: t('finished'), color: experimentColors.finished },
    { value: 'rejected', label: t('rejected'), color: experimentColors.rejected }
  ];

  const handleDropdownChange = async (newStatus) => {
    if (mode === 'individual' && newStatus === currentStatus) return;
    
    setIsChanging(true);
    try {
      await onStatusChange(newStatus);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <MemoizedBlazingFastDropdown
      options={statusOptions}
      value={mode === 'bulk' ? '' : currentStatus}
      placeholder={t('move')}
      onChange={handleDropdownChange}
      disabled={disabled || isChanging}
      loading={loading || isChanging}
      size={size}
      mode={mode}
    />
  );
};

export default React.memo(StatusDropdown, (prevProps, nextProps) => {
  // Only re-render if essential props change
  return (
    prevProps.currentStatus === nextProps.currentStatus &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.loading === nextProps.loading &&
    prevProps.mode === nextProps.mode &&
    prevProps.selectedCount === nextProps.selectedCount &&
    prevProps.size === nextProps.size
  );
});